import { Box } from '@chakra-ui/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import {
  recipientLabel,
  summarizeRecipients,
  RECIPIENT_INLINE_LIMIT,
  type ConversationRecipient,
} from './conversationRecipients';

interface RecipientSummaryTextProps {
  /** "CC:" / "BCC:" — the copy that introduces the list. */
  label?: string;
  recipients?: ConversationRecipient[];
  /** How many names to print before counting the rest. */
  limit?: number;
  /**
   * The row has a line to itself rather than sitting inside the header's
   * sentence. It then gets a box that clips, and its names are darkened against
   * the label introducing them.
   */
  ownsLine?: boolean;
  testId?: string;
}

const moreLabel = (count: number) => `+${count} more`;

/**
 * A recipient list in the conversation header.
 *
 * Prints the first few names and counts the rest behind a hover, so an email
 * copying fifteen teammates still reads as one line about a message rather than
 * four lines of names. Nothing is lost — the tooltip lists every hidden name.
 *
 * The cut is a fixed number of names, not as many as the line happens to fit.
 * Measuring read better in principle and did not survive the header: this row is
 * a flex item whose ancestors default to `min-width: auto`, so fourteen nowrap
 * names set a 1595px floor that the 815px card could not shrink below. The row
 * then measured *itself* at 1595px, concluded every name fit, and cut nothing —
 * while dragging the attachment grid below it to twice its width. A fixed cap
 * cannot be talked into that by its own content.
 *
 * `@kula-ai/components` has no primitive for this (AvatarGroup counts avatars,
 * not text), so it is local. It looks like it belongs upstream.
 */
export const RecipientSummaryText = ({
  label,
  recipients = [],
  limit = RECIPIENT_INLINE_LIMIT,
  ownsLine = false,
  testId,
}: RecipientSummaryTextProps) => {
  const { all, shown, hidden } = summarizeRecipients(recipients, limit);

  if (all.length === 0) return null;

  const hiddenNames = hidden.map(recipientLabel).join(', ');

  const more = hidden.length > 0 && (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* Chakra's Box, not a text component: `asChild` hands the trigger its
            ref, and the line around it already owns the type. */}
        <TooltipTrigger asChild>
          <Box
            as="span"
            // The one word on the line darker than the names it stands for —
            // the part that answers on hover rather than on sight.
            color="secondary-800"
            textDecoration="underline"
            textDecorationStyle="dotted"
            cursor="default"
            whiteSpace="nowrap"
            aria-label={hiddenNames}
            data-test-id={testId ? `${testId}-more` : undefined}
          >
            {moreLabel(hidden.length)}
          </Box>
        </TooltipTrigger>
        <TooltipContent side="top" collisionPadding={12}>
          {hiddenNames}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Box
      as="span"
      /**
       * A row of its own only where it owns the line, so the names can be given
       * somewhere to shrink to without the header's sentence breaking into
       * three — a block here stranded the recipient and the timestamp on lines
       * of their own.
       */
      {...(ownsLine ? { display: 'flex', alignItems: 'baseline' } : {})}
      data-test-id={testId}
    >
      <Box
        as="span"
        /**
         * `minW={0}` is the load-bearing part: a flex item's floor is its own
         * content, so without it the nowrap names refuse to shrink and push the
         * card wide instead of being clipped by it.
         */
        {...(ownsLine
          ? {
              flex: '0 1 auto',
              minW: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }
          : {})}
      >
        {label ? `${label} ` : null}

        {shown.map((recipient, index) => (
          <Box
            as="span"
            /**
             * Only on the lines this component owns.
             *
             * There the names are darkened against the label that introduces
             * them. In the header's sentence they are one phrase among several
             * that the sentence has already styled, and setting a colour and a
             * weight here overrode it.
             */
            {...(ownsLine ? { color: 'secondary-800', fontWeight: 600 } : {})}
            key={`${recipientLabel(recipient)}-${index}`}
          >
            {recipientLabel(recipient)}
            {index < shown.length - 1 ? ', ' : ''}
          </Box>
        ))}
      </Box>

      {/* Never the part that gets clipped: the count is the only thing saying
          there is more to see, so the names give way before it does. */}
      {more ? (
        <Box as="span" flex="none" ml="4px">
          {more}
        </Box>
      ) : null}
    </Box>
  );
};
